import get from 'lodash/get';
import set from 'lodash/set';
import {myExperiment, scoreDetails, redeemPoints} from '@/api/experiments';

// State.
export const state = {
  experiment: null,
  group: null,
  quit: false,
  experimentProfile: null,
  aside: null,
  scoreDetails: null,
  tenant: {
    name: null,
    allowMultipleExperiments: false,
  },
  referenceLabeling: {
    currentArtifactDatasetIndex: 0,
    selectedOrderBy: 'updated_at',
    meta: {
      next_page: null,
      current_page: 1,
      has_more_pages: true,
      has_previous_pages: false,
      previous_page: null,
    }
  }
}

// Getters.
export const getters = {
  tenantName: state => {
    let name = get(state, ['tenant', 'name'], 'Civium');

    if (name === null) {
      return 'Civium';
    }

    return name;
  },

  allowMultipleExperiments: state => get(state, ['tenant', 'allowMultipleExperiments'], false),

  currentExperimentId: state => get(state, ['experiment', 'id'], null),

  hasExperiment: state => state.experiment && state.experiment.id,

  hasGroup: state => get(state.group, ['id'], null) !== null,

  wantToQuit: state => state.quit,

  experimentAlwaysRunning: state => get(state.experiment, ['is_always_running'], false),
  experimentCanBeQuitEarly: (state, getters) => !getters.experimentAlwaysRunning,

  minutesToResume: state => get(state.experimentProfile, ['minutes_to_resume'], 10),
  thresholdToRespond: state => get(state.experimentProfile, ['minutes_to_respond'], 1) * 60,
  thresholdToQuit: state => get(state.experimentProfile, ['minutes_to_resume'], 10) * 60,

  waitingForGuardianConsent: state => get(state.experimentProfile, ['is_waiting_for_guardian_consent'], false),
  experimentProfile: state => state.experimentProfile,
  experimentIsCompleted: state => get(state.experimentProfile, ['completed_at']) !== null,
  experientWasQuit: state => get(state.experimentProfile, ['quit_at']) !== null,
  experimentIsOnGoing: (state, getters) => (!getters.experimentIsCompleted && !getters.experientWasQuit),
  hasStagePercentage: state => get(state.experimentProfile, ['has_stage_percentage']) === true,
  stageStarted: state => get(state.experimentProfile, ['stage_started']) === true,
  sensitivityPercentage: state => get(state.experimentProfile, ['sensitivity_percentage'], 0),
  dedicatedTo: state => get(state.experimentProfile, ['dedicated_to']),

  asideId: state => get(state.aside, ['id']),
  hasAside: state => get(state.aside, ['id'], null) !== null,

  referenceLabelingMeta: state => state.referenceLabeling.meta,
  referenceLabelingSelectedOrderBy: state => state.referenceLabeling.selectedOrderBy,
  referenceLabelingCurrentArtifactDatasetIndex: state => state.referenceLabeling.currentArtifactDatasetIndex,

  numberOfUploads: state => get(state.experimentProfile, ['number_of_uploads'], 0),
}

// Mutations.
export const mutations = {
  SET_EXPERIMENT: (state, {experiment}) => state.experiment = experiment,

  SET_GROUP: (state, {group}) => state.group = group,

  SET_QUIT: (state, {quit}) => state.quit = quit,

  UPDATE_EXPERIMENT_PROFILE: (state, {profile}) => state.experimentProfile = profile,

  SET_ASIDE: (state, componentName) => state.aside = componentName,

  SET_SCORE_DETAILS: (state, details) => state.scoreDetails = details,

  SET_REFERENCE_LABELING_META: (state, meta) => state.referenceLabeling.meta = meta,

  SET_REFERENCE_LABELING_SELECTED_ORDER_BY: (state, selectedOrderBy) => state.referenceLabeling.selectedOrderBy = selectedOrderBy,

  SET_REFERENCE_LABELING_CURRENT_ARTIFACT_DATASET_INDEX: (state, index) => state.referenceLabeling.currentArtifactDatasetIndex = index,

  SET_NUMBER_OF_UPLOADS: (state, numberOfUploads) => set(state, ['experimentProfile', 'number_of_uploads'], numberOfUploads),

  SET_TENANT_DETAILS: (state, {name, allowMultipleExperiments}) => {
    set(state.tenant, ['name'], name);
    set(state.tenant, ['allowMultipleExperiments'], allowMultipleExperiments);
  }
}

// Actions.
export const actions = {
  fetchExperiment: async ({commit}) => {
    let {data: {experiment, group, profile}} = await myExperiment();

    commit('SET_EXPERIMENT', {experiment});
    commit('SET_GROUP', {group});
    commit('UPDATE_EXPERIMENT_PROFILE', {profile});
  },

  enableQuit: ({commit}) => commit('SET_QUIT', {quit: false}),

  quitExperiment: ({commit}) => {
    commit('SET_QUIT', {quit: true});
    commit('SET_EXPERIMENT', {experiment: null});
  },

  updateExperimentProfile: ({commit}, profile) => commit('UPDATE_EXPERIMENT_PROFILE', {profile}),

  openAside: ({commit}, aside) => commit('SET_ASIDE', aside),
  closeAside: ({commit}) => commit('SET_ASIDE', null),

  fetchScoreDetails: async ({commit, state}) => {
    let {data} = await scoreDetails({experiment: state.experiment.id});
    commit('SET_SCORE_DETAILS', data);
  },

  performRedeemPoints: async ({commit, state}) => {
    let {data} = await redeemPoints({experiment: state.experiment.id});
    commit('SET_SCORE_DETAILS', data);
  },

  setReferenceLabelingMeta: ({commit}, {meta}) => {
    commit('SET_REFERENCE_LABELING_META', meta);
  },

  setReferenceLabelingSelectedOrderBy: ({commit}, {selectedOrderBy}) => {
    commit('SET_REFERENCE_LABELING_SELECTED_ORDER_BY', selectedOrderBy);
  },

  setReferenceLabelingCurrentIndex: ({commit}, {index}) => {
    commit('SET_REFERENCE_LABELING_CURRENT_ARTIFACT_DATASET_INDEX', index);
  },

  setTenantDetails: async ({commit}, {name, allowMultipleExperiments}) => {
    commit('SET_TENANT_DETAILS', {name, allowMultipleExperiments});
  },

  incrementNumberOfUploads: ({commit, getters}) => {
    let numberOfUploads = getters.numberOfUploads;
    numberOfUploads++;

    commit('SET_NUMBER_OF_UPLOADS', numberOfUploads)
  }
}
